import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaSave, FaAndroid, FaApple } from 'react-icons/fa';
import { API_BASE } from '../../utils/helpers';

const PLATFORMS = [
  { key: 'android', label: 'Android', icon: FaAndroid, tone: 'text-emerald-600' },
  { key: 'ios', label: 'iOS', icon: FaApple, tone: 'text-slate-700' },
];

function PlatformCard({ platform, label, icon: Icon, tone, data, onSave }) {
  const [form, setForm] = useState(data);
  const [saving, setSaving] = useState(false);
  useEffect(() => setForm(data), [data]);

  const change = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const submit = async () => { setSaving(true); await onSave(platform, form); setSaving(false); };

  return (
    <div className="ui-card p-5">
      <div className="mb-4 flex items-center gap-2">
        <Icon className={`text-lg ${tone}`} />
        <h3 className="text-sm font-semibold text-slate-900">{label}</h3>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Minimum version (force update below this)</label>
          <input className="ui-input" name="min_version" value={form.min_version || ''} onChange={change} placeholder="2.6.0" />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Latest version (suggest update below this)</label>
          <input className="ui-input" name="latest_version" value={form.latest_version || ''} onChange={change} placeholder="2.6.3" />
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1 block text-sm font-medium text-slate-700">Store URL</label>
          <input className="ui-input" name="store_url" value={form.store_url || ''} onChange={change} placeholder="https://…" />
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1 block text-sm font-medium text-slate-700">Update message (optional)</label>
          <input className="ui-input" name="update_message" value={form.update_message || ''} onChange={change} placeholder="A new version is available with improvements." />
        </div>
      </div>
      <div className="mt-4">
        <button className="ui-btn-primary" onClick={submit} disabled={saving}>
          <FaSave className="text-xs" /> {saving ? 'Saving…' : `Save ${label}`}
        </button>
      </div>
    </div>
  );
}

export default function AppVersion() {
  const [config, setConfig] = useState({ android: {}, ios: {} });
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState({ type: '', text: '' });

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/api/app/version`);
      setConfig(res.data?.config || { android: {}, ios: {} });
    } catch (e) {
      setMsg({ type: 'error', text: 'Failed to load version config.' });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (platform, form) => {
    try {
      const res = await axios.put(`${API_BASE}/api/app/version`, { platform, ...form });
      setConfig(res.data?.config || config);
      setMsg({ type: 'success', text: `${platform === 'ios' ? 'iOS' : 'Android'} version settings saved.` });
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.error || 'Failed to save.' });
    } finally {
      setTimeout(() => setMsg({ type: '', text: '' }), 3000);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-slate-900">App Version</h2>
        <p className="text-sm text-slate-500">
          Control the force-update / suggested-update prompts shown in the mobile app.
          Below <b>minimum</b> = blocking "Update required"; below <b>latest</b> = dismissible "Update available".
        </p>
      </div>

      {msg.text && (
        <div className={`mb-4 rounded-lg border px-4 py-2.5 text-sm ${
          msg.type === 'success' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-rose-200 bg-rose-50 text-rose-700'
        }`}>{msg.text}</div>
      )}

      {loading ? (
        <div className="ui-card h-40 animate-pulse" />
      ) : (
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          {PLATFORMS.map((p) => (
            <PlatformCard
              key={p.key}
              platform={p.key}
              label={p.label}
              icon={p.icon}
              tone={p.tone}
              data={config[p.key] || {}}
              onSave={save}
            />
          ))}
        </div>
      )}
    </div>
  );
}
